import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { FormBuilderComponent as FormioBuilderComponent } from '@formio/angular';
import { ClinicForm } from '@models/forms/clinic-form';
import { FormType, formTypeNames } from '@models/forms/form-type.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  EditEvent,
  GridComponent,
  GridDataResult,
  RemoveEvent,
  RowClassArgs,
  SelectionEvent,
} from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { ActionPanelService } from '@services/actionpanel.service';
import { ClinicFormService } from '@services/form.service';
import { SharedFormService } from '@services/shared-form.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ShareFormComponent } from './../share-form/share-form.component';
import { ConfirmFormDeleteComponent } from './confirm-form-delete/confirm-form-delete.component';

@Component({
  selector: 'app-forms-list',
  templateUrl: './forms-list.component.html',
  styleUrls: ['./forms-list.component.less'],
})
export class FormsListComponent implements OnInit, OnDestroy {
  @ViewChild(GridComponent) private grid: GridComponent;
  @ViewChild('builder') builder: FormioBuilderComponent;
  @ViewChild('activateFormCheckbox') activateFormCheckbox: ElementRef<HTMLInputElement>;

  searchValue = '';
  searchCtrl: FormControl;
  filteredForms: Observable<ClinicForm[]>;
  disableTabsAndGrid = false;
  editMode = false;
  loading = false;
  forms: ClinicForm[];
  selectedRowIndex: number;
  selectedDataItem: ClinicForm = null;
  editingDataItem: ClinicForm = null;
  selectedFormType: FormType;
  FormType = FormType;
  formTypeNames = formTypeNames;
  selectedKeys: number[] = [];
  formRefreshed$ = new Subject<ClinicForm>();
  unsub = new Subject<void>();

  gridView: GridDataResult;
  gridState: State = {
    sort: [],
    skip: 0,
    take: 10,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  constructor(
    private formService: ClinicFormService,
    private dialog: MatDialog,
    private router: Router,
    private modalService: NgbModal,
    private actionPanelService: ActionPanelService,
    private sharedFormService: SharedFormService
  ) {}

  ngOnInit() {
    this.loading = false;
    this.disableTabsAndGrid = false;
    this.searchCtrl = new FormControl();
    this.initSubscriptions();
    this.onSelectTab(FormType.Consent);
  }

  private initSubscriptions() {
    // Added
    this.formService.formAdded.pipe(takeUntil(this.unsub)).subscribe((form: ClinicForm) => {
      this.selectFormInTab(form);
      this.editFormHandler(form);
    });

    // Updated
    this.formService.formUpdated.pipe(takeUntil(this.unsub)).subscribe((form: ClinicForm) => {
      this.selectFormInTab(form);
    });

    // Deleted
    this.formService.formDeleted.pipe(takeUntil(this.unsub)).subscribe((form: ClinicForm) => {
      if (this.selectedFormType === form.formType) {
        this.loadForms(this.selectedFormType);
      }
    });

    // Form Change Cancelled
    this.formService.formChangeCancelled.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.loading = false;
      this.disableTabsAndGrid = false;
    });

    // Activated
    this.formService.formActivated.pipe(takeUntil(this.unsub)).subscribe((form: ClinicForm) => {
      if (this.selectedFormType === form.formType) {
        this.loadForms(this.selectedFormType);
      }
    });

    // Deactivated
    this.formService.formDeactivated.pipe(takeUntil(this.unsub)).subscribe((form: ClinicForm) => {
      if (this.selectedFormType === form.formType) {
        this.loadForms(this.selectedFormType);
      }
    });

    // Shared Form Added From Library
    this.sharedFormService.sharedFormAddedFromLibrary.pipe(takeUntil(this.unsub)).subscribe((form: ClinicForm) => {
      if (form.formType) {
        if (this.selectedFormType === form.formType) this.loadForms(form.formType);
      }
    });

    // Action Panel Closed
    this.actionPanelService.actionPanelClosed$.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.loading = false;
      this.disableTabsAndGrid = false;
    });
  }

  private selectFormInTab(form: ClinicForm) {
    this.onSelectTab(form.formType);
    this.selectRow(form);
  }

  private isTemplateLibraryOpen(): boolean {
    return this.router.url.indexOf('(action-panel-secondary:form-library-overview)') !== -1 ? true : false;
  }

  private async loadForms(formType: FormType) {
    this.loading = true;
    const forms = await this.formService.getForms(true, [formType]).toPromise();
    this.forms = forms;

    this.gridView = {
      data: this.forms.slice(),
      total: this.forms.length,
    };

    this.filteredForms = this.searchCtrl.valueChanges.pipe(
      startWith(''),
      map((form) => this.filterForms(form))
    );

    if (!this.isTemplateLibraryOpen()) {
      this.loading = false;
    }
  }

  private selectRow(dataItem: ClinicForm) {
    this.selectedDataItem = dataItem;
    this.formRefreshed$.next(this.selectedDataItem);
    if (this.selectedKeys.length > 0) {
      this.selectedKeys = [];
    }
    this.selectedKeys.push(this.selectedDataItem.id);
  }

  private filterForms(name: string) {
    let filterResults: ClinicForm[] = [];

    if (name !== '') {
      this.gridView = {
        data: this.forms.filter((form) => form.name.toLowerCase().includes(name.toLowerCase())),
        total: this.forms.filter((form) => form.name.toLowerCase().includes(name.toLowerCase())).length,
      };
      filterResults = this.forms.filter((form) => form.name.toLowerCase().includes(name.toLowerCase()));
    } else {
      this.gridView = {
        data: this.forms,
        total: this.forms.length,
      };
      filterResults = [];
    }
    return filterResults;
  }

  onSelectTab(formType: FormType) {
    this.editMode = false;
    this.selectedKeys = [];
    this.selectedDataItem = null;
    this.loadForms(formType);
    this.selectedFormType = formType;
  }

  onSelectRow(e: SelectionEvent) {
    this.editMode = false;
    if (e.selectedRows.length > 0) {
      this.selectRow(e.selectedRows[0].dataItem);
    }
  }

  editHandler(args: EditEvent) {
    this.editMode = false;
    this.selectRow(args.dataItem);
    this.router.navigate(['/management/forms', { outlets: { 'action-panel': ['edit-form', args.dataItem.id] } }]);
  }

  editFormHandler(dataItem: ClinicForm, event?: Event) {
    event?.stopPropagation();
    this.disableTabsAndGrid = true;
    this.editMode = true;
    this.editingDataItem = structuredClone(dataItem);
    this.selectRow(dataItem);
  }

  addHandler() {
    this.router.navigate([
      '/management/forms',
      {
        outlets: {
          'action-panel': ['edit-form', this.formService.defaultId],
        },
      },
    ]);
  }

  saveHandler(): void {
    const form = this.editingDataItem;
    this.formService
      .updateForm(form)
      .pipe(takeUntil(this.unsub))
      .subscribe((updatedForm) => {
        this.selectedDataItem = updatedForm;
        this.editMode = false;
        this.disableTabsAndGrid = false;
      });
  }

  cancelHandler() {
    this.editMode = false;
    this.disableTabsAndGrid = false;
  }

  cloneFormHandler(formId: number) {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '325px',
      data: {
        title: 'Clone eForm?',
        content: 'Are you sure you want to clone this eForm?',
        confirmButtonText: 'Clone',
        showCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'confirm') {
          this.loading = true;
          this.formService.cloneForm(formId).subscribe((newForm: ClinicForm) => {});
        }
      });
  }

  deactivateHandler(args: RemoveEvent): void {
    if (this.selectedKeys.length > 0) {
      this.selectedKeys = [];
    }
    this.selectedKeys.push(args.dataItem.id);
    this.editMode = false;
    this.selectRow(args.dataItem);

    this.formService
      .isFormInUse(args.dataItem.id)
      .pipe(takeUntil(this.unsub))
      .subscribe((isInUse) => {
        const dialogRef = this.dialog.open(ConfirmFormDeleteComponent, {
          width: '400px',
          data: { isInUse },
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.unsub))
          .subscribe((result) => {
            if (result === 'deactivate') {
              args.dataItem.isDeleted = true;
              this.formService.deactivateForm(args.dataItem).subscribe();
            } else if (result === 'delete') {
              this.formService.deleteForm(args.dataItem).subscribe();
            }
          });
      });
  }

  activateFormHandler(form: ClinicForm) {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '325px',
      data: {
        title: 'Activate eForm?',
        content: 'Are you sure you want to activate this eForm?',
        confirmButtonText: 'Activate',
        showCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'confirm') {
          form.isDeleted = false;
          this.formService.activateForm(form).subscribe(() => {});
        } else {
          this.activateFormCheckbox.nativeElement.checked = false;
          this.activateFormCheckbox.nativeElement.indeterminate = false;
        }
      });
  }

  shareFormHandler(form: ClinicForm) {
    let shareFormModal = this.modalService.open(ShareFormComponent, {
      centered: true,
    });
    (shareFormModal.componentInstance as ShareFormComponent).formToShare = form;
  }

  addFromTemplateHandler() {
    this.loading = true;
    this.router.navigate(['/management/forms', { outlets: { 'action-panel-secondary': ['form-library-overview'] } }]);
  }

  rowCallback = (context: RowClassArgs) => {
    if (context.dataItem.isDeleted) return { disabled: true };
    else return {};
  };

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
